import { formatLoadedPostsKey } from "./formatLoadedPostsKey";
import { growtypePostAjaxLoadMoreContent } from "../events/growtypePostAjaxLoadMoreContent";
import { adjustPostsGrid } from "./adjustPostsGrid";
import { filterPosts } from "./filterPosts";

const currentRequests = new Map();
let ephemeralRequestSession;

function getRequestSession() {
    const storageKey = 'growtype_post_request_session';

    try {
        let requestSession = window.sessionStorage.getItem(storageKey);

        if (!requestSession) {
            requestSession = window.crypto && window.crypto.randomUUID
                ? window.crypto.randomUUID()
                : Date.now().toString(36) + Math.random().toString(36).slice(2);
            window.sessionStorage.setItem(storageKey, requestSession);
        }

        return requestSession;
    } catch (error) {
        if (!ephemeralRequestSession) {
            ephemeralRequestSession = 'ephemeral-' + Date.now().toString(36) + Math.random().toString(36).slice(2);
        }

        return ephemeralRequestSession;
    }
}

function stableStringify(value) {
    if (Array.isArray(value)) {
        return '[' + value.map(stableStringify).join(',') + ']';
    }

    if (value && typeof value === 'object') {
        return '{' + Object.keys(value).sort().map(function (key) {
            return JSON.stringify(key) + ':' + stableStringify(value[key]);
        }).join(',') + '}';
    }

    return JSON.stringify(value);
}

export function loadMorePosts(elements, args = {}) {
    let wrapper = elements['posts_container'].closest('.growtype-post-container-wrapper');
    let wrapperId = wrapper.attr('id');
    let displayHiddenPosts = args['display_hidden_posts'] ?? false;

    if (typeof elements['filters_container'] === 'undefined') {
        elements['filters_container'] = wrapper.find('.growtype-post-terms-filters');
    }

    if (typeof elements['btn'] === 'undefined') {
        elements['btn'] = wrapper.find('.btn-loadmore');
    }

    args['cursor'] = args['reset_feed']
        ? 0
        : parseInt(elements['posts_container'].attr('data-next-cursor') ?? args['cursor'] ?? 0, 10);

    if (typeof args['orderby'] === 'undefined') {
        if (wrapper.find('select.growtype-post-custom-filter[name="orderby"]').length > 0) {
            args['orderby'] = elements['posts_container'].closest('.growtype-post-container-wrapper').find('select.growtype-post-custom-filter[name="orderby"]').val();
        }
    }

    if (typeof args['amount_to_load'] === 'undefined') {
        args['amount_to_load'] = args['posts_per_page'];
    }

    let requestSession = getRequestSession();
    let requestSignature = stableStringify(args);
    let existingRequest = currentRequests.get(wrapperId);

    if (existingRequest && existingRequest.signature === requestSignature) {
        return existingRequest.promise;
    }

    if (existingRequest) {
        existingRequest.xhr.abort();
    }

    let btn = elements['btn'] || null;
    let actionsWrapper = btn ? btn.closest('.gp-actions-wrapper') : $();
    let infiniteLoadingStatus = actionsWrapper.find('.gp-infinite-loading-status');
    let wrapperState = window.growtype_post['wrappers'][wrapperId] || {};
    wrapperState['load_more_posts_request_in_flight'] = true;
    window.growtype_post['wrappers'][wrapperId] = wrapperState;

    if (btn) {
        btn.addClass('is-loading');

        if (btn.find('.spinner-border').length === 0) {
            btn.append('<span class="spinner-border"></span>');
        }
    }

    if (infiniteLoadingStatus.length > 0) {
        actionsWrapper.addClass('is-loading');
        infiniteLoadingStatus.prop('hidden', false);
    }

    let currentRequest;
    let requestPromise = new Promise((resolve, reject) => {
        function finishRequest() {
            let activeRequest = currentRequests.get(wrapperId);

            if (activeRequest && activeRequest.xhr === currentRequest) {
                currentRequests.delete(wrapperId);
                let currentWrapperState = window.growtype_post['wrappers'][wrapperId] || {};
                currentWrapperState['load_more_posts_request_in_flight'] = false;
                currentWrapperState['load_more_posts_btn_clicked'] = false;
            }

            if (btn) {
                btn.removeClass('is-loading');
                btn.find('.spinner-border').remove();
            }

            if (infiniteLoadingStatus.length > 0) {
                actionsWrapper.removeClass('is-loading');
                infiniteLoadingStatus.prop('hidden', true);
            }
        }

        currentRequest = jQuery.ajax({
            url: growtype_post.ajax_url,
            type: 'post',
            data: {
                action: 'growtype_post_load_more_posts',
                nonce: growtype_post.nonce,
                request_feed: wrapperId,
                request_session: requestSession,
                args: args
            },
            success: function (response) {
                if (!response || response.success !== true || !response.data) {
                    finishRequest();
                    reject(new Error(response?.data?.message || 'Unable to load more posts.'));
                    return;
                }

                if (displayHiddenPosts && args['selected_terms_navigation_values']) {
                    let posts = wrapper.find('.growtype-post-single:hidden');

                    let values = [];
                    Object.entries(args['selected_terms_navigation_values']).map(function (value, index) {
                        values[value[0]] = {
                            value: value[1].toString(),
                        };
                    });

                    filterPosts(posts, values)
                }

                if (response.data.render) {
                    if (args['reset_feed'] || (args['shuffle'] !== undefined && args['shuffle'] === true)) {
                        elements['posts_container'].html('');
                    }

                    let content = elements['posts_container'].append(response.data.render);
                    wrapper = content.closest('.growtype-post-container-wrapper')
                }

                elements['posts_container'].attr('data-next-cursor', response.data.next_cursor ?? args['cursor']);

                if (btn) {
                    btn.closest('.gp-actions-wrapper').show();
                }

                if (!response.data.render || response.data.render === "" || response.data.has_more === false) {
                    if (btn) {
                        btn.closest('.gp-actions-wrapper').hide();
                    }

                    let loadedPostsKey = formatLoadedPostsKey(elements['filters_container']);

                    if (window.growtype_post['wrappers'][wrapperId]['loaded_posts'] === undefined) {
                        window.growtype_post['wrappers'][wrapperId]['loaded_posts'] = {};
                    }

                    window.growtype_post['wrappers'][wrapperId][loadedPostsKey] = true;
                }

                adjustPostsGrid(wrapper);

                document.dispatchEvent(growtypePostAjaxLoadMoreContent({
                    response: response,
                    wrapper: wrapper,
                    args: args
                }));

                finishRequest();
                resolve(response);
            },
            error: function (jqXHR, textStatus, errorThrown) {
                finishRequest();

                if (textStatus === 'abort') {
                    resolve();
                    return;
                }

                const message = typeof errorThrown === 'string' && errorThrown.length
                    ? errorThrown
                    : 'Unable to load more posts (' + (textStatus || 'network error') + ').';
                reject(new Error(message));
            }
        });
    });

    // Callers may intentionally fire-and-forget this request. Keep the
    // original rejection available to consumers without reporting it as an
    // unhandled browser error when the network request fails first.
    requestPromise.catch(function () {});

    currentRequests.set(wrapperId, {
        signature: requestSignature,
        xhr: currentRequest,
        promise: requestPromise
    });

    return requestPromise;
}
